// Captures Google Ads click IDs (gclid / gbraid / wbraid) and stores them for 90 days
const KEYS = ['gclid', 'gbraid', 'wbraid'] as const;
const PREFIX = 'isr_';
const DAYS = 90;
const COOKIE_DOMAIN = '.mtalkz.com'; // shares IDs across subdomains (www, app, etc.)

export type ClickIds = Record<(typeof KEYS)[number], string>;

function setCookie(name: string, value: string, days: number) {
	const d = new Date();
	d.setTime(d.getTime() + days * 864e5);
	const domain = window.location.hostname.endsWith('mtalkz.com')
		? `; domain=${COOKIE_DOMAIN}`
		: '';
	document.cookie = `${name}=${encodeURIComponent(value)}; expires=${d.toUTCString()}; path=/${domain}; SameSite=Lax; Secure`;
}

function safeDecode(value: string) {
	try {
		return decodeURIComponent(value);
	} catch {
		return value;
	}
}

function getCookie(name: string) {
	try {
		const m = document.cookie.match(
			new RegExp('(?:^|; )' + name + '=([^;]*)'),
		);
		return m ? safeDecode(m[1] ?? '') : '';
	} catch {
		return '';
	}
}

// Call once on every page load
export function captureClickIds() {
	if (typeof window === 'undefined') return;
	const params = new URLSearchParams(window.location.search);
	const found = KEYS.filter(k => params.get(k));
	if (!found.length) return;

	// New ad click: clear old IDs so values from different clicks never mix
	KEYS.forEach(k => setCookie(PREFIX + k, '', -1));
	found.forEach(k =>
		setCookie(PREFIX + k, params.get(k)!.slice(0, 255), DAYS),
	);
}

// Call inside every form submit
export function getClickIds(): ClickIds {
	const ids: ClickIds = { gclid: '', gbraid: '', wbraid: '' };
	if (typeof window === 'undefined') return ids;

	const params = new URLSearchParams(window.location.search);
	KEYS.forEach(k => {
		ids[k] = params.get(k) || getCookie(PREFIX + k) || '';
	});

	// Fallback: Google's own cookies set by the Conversion Linker (format: GCL.<timestamp>.<id>)
	if (!ids.gclid && !ids.gbraid && !ids.wbraid) {
		const aw = getCookie('_gcl_aw');
		if (aw) ids.gclid = aw.split('.').slice(2).join('.');
		const gb = getCookie('_gcl_gb');
		if (gb) ids.gbraid = gb.split('.').slice(2).join('.');
	}
	return ids;
}
